import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Easing } from 'react-native';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';

/**
 * Skeleton loading placeholder for MobilePaymentCard (light theme)
 * Shows animated shimmer effect while data is loading
 */
export const MobilePaymentCardLightSkeleton: React.FC = () => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.ease,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 1000,
          easing: Easing.ease,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [shimmerAnim]);

  const opacity = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.4, 0.8],
  });

  const SkeletonBox: React.FC<{ width: number | string; height: number; style?: any }> = ({ width, height, style }) => (
    <Animated.View
      style={[
        styles.skeletonBox,
        { width, height, opacity },
        style,
      ]}
    />
  );

  return (
    <View style={styles.card}>
      <View style={styles.cardContent}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {/* Resident Name */}
            <SkeletonBox width={120} height={20} style={{ marginBottom: 8 }} />
            {/* Property Info */}
            <SkeletonBox width={180} height={14} />
          </View>
          {/* Autopay Badge Placeholder */}
          <SkeletonBox width={70} height={24} style={styles.autopayBadge} />
        </View>

        {/* Balance */}
        <View style={styles.balanceSection}>
          <SkeletonBox width={100} height={32} style={{ marginBottom: 4 }} />
          <SkeletonBox width={80} height={14} />
        </View>

        {/* Button */}
        <SkeletonBox width="100%" height={44} style={styles.buttonSkeleton} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginHorizontal: 16,
    marginVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
    overflow: 'hidden',
  },
  cardContent: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    flex: 1,
    marginRight: 12,
  },
  skeletonBox: {
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
  },
  autopayBadge: {
    borderRadius: 12,
  },
  balanceSection: {
    marginBottom: 12,
  },
  buttonSkeleton: {
    borderRadius: 8,
  },
});

export default MobilePaymentCardLightSkeleton;
